import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Edit, Trash, Phone, Mail, Calendar, User, CreditCard, FileText, DollarSign, TrendingUp, AlertCircle, Shield } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../utils/axios.ts';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';

function getInitials(name) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

function formatDateDMY(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB');
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-KE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
}

function StatusBadge({ is_active }) {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
        is_active
          ? 'bg-green-100 text-green-800'
          : 'bg-red-100 text-red-800'
      }`}
    >
      {is_active ? 'Active' : 'Inactive'}
    </span>
  );
}

const MemberProfilePage = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [loans, setLoans] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [fines, setFines] = useState([]);
  const [loadingTab, setLoadingTab] = useState(false);

  useEffect(() => {
    fetchMemberDetails();
  }, [memberId]);

  useEffect(() => {
    if (member && activeTab !== 'info') {
      fetchTabData(activeTab);
    }
  }, [activeTab, member]);

  const fetchMemberDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/members/${memberId}`);
      setMember(response.data.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load member details');
      toast.error('Failed to load member details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async (tab) => {
    setLoadingTab(true);
    try {
      switch (tab) {
        case 'loans':
          const loansResponse = await api.get(`/members/${memberId}/loans`);
          setLoans(loansResponse.data.data || []);
          break;
        case 'contributions':
          const contributionsResponse = await api.get(`/members/${memberId}/contributions`);
          setContributions(contributionsResponse.data.data || []);
          break;
        case 'fines':
          const finesResponse = await api.get(`/members/${memberId}/fines`);
          setFines(finesResponse.data.data || []);
          break;
        default:
          break;
      }
    } catch (err) {
      toast.error(`Failed to load ${tab} data`);
    } finally {
      setLoadingTab(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${member.name}? This action cannot be undone.`)) {
      try {
        await api.delete(`/members/${memberId}`);
        toast.success('Member deleted successfully');
        navigate('/members');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to delete member');
      }
    }
  };

  const handleDownloadStatement = async () => {
    const toastId = toast.loading('Generating statement...');
    try {
      const response = await api.get(`/contributions/member-statement-admin/${memberId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement-${member.name.replace(/\s+/g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('Statement downloaded successfully', { id: toastId });
    } catch (err) {
      toast.error('Failed to download statement', { id: toastId });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading member profile...</p>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Member Not Found</h2>
          <p className="text-gray-600 mb-4">{error || 'The member you are looking for does not exist.'}</p>
          <Link
            to="/members"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Members
          </Link>
        </div>
      </div>
    );
  }

  const memberSince = (() => {
    const registeredDate = new Date(member.date_registered);
    const now = new Date();
    const diffTime = Math.abs(now - registeredDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''} ${months > 0 ? `${months} month${months > 1 ? 's' : ''}` : ''}`;
    } else if (months > 0) {
      return `${months} month${months > 1 ? 's' : ''}`;
    } else {
      return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    }
  })();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/members"
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft size={20} className="mr-2" />
                <span className="font-medium">Members</span>
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-900 font-medium">View Member</span>
            </div>
            {isAdmin && (
              <div className="flex space-x-2">
                <button
                  onClick={handleDownloadStatement}
                  className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  <FileText size={16} className="mr-2" />
                  Statement
                </button>
                <button
                  onClick={() => navigate(`/members?edit=${memberId}`)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit size={16} className="mr-2" />
                  Edit
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  <Trash size={16} className="mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Member Header Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <div className="flex items-start space-x-6">
            <div className="h-32 w-32 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-4xl font-bold shadow-lg">
              {getInitials(member.name)}
            </div>
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{member.name}</h1>
                  <p className="text-lg text-gray-600 mb-3">{member.phone_number}</p>
                  <StatusBadge is_active={member.is_active} />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Member No.</p>
                  <p className="text-sm font-semibold text-gray-900">{member.member_no || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">ID Number</p>
                  <p className="text-sm font-semibold text-gray-900">{member.id_no || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Registered</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDateDMY(member.date_registered)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Member Since</p>
                  <p className="text-sm font-semibold text-gray-900">{memberSince}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button 
                onClick={() => setActiveTab('info')}
                className={`border-b-2 py-4 px-1 text-sm font-medium flex items-center ${
                  activeTab === 'info' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <User size={16} className="mr-2" />
                Personal Info
              </button>
              <button 
                onClick={() => setActiveTab('loans')}
                className={`border-b-2 py-4 px-1 text-sm font-medium flex items-center ${
                  activeTab === 'loans' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <DollarSign size={16} className="mr-2" />
                Loans
              </button>
              <button 
                onClick={() => setActiveTab('contributions')}
                className={`border-b-2 py-4 px-1 text-sm font-medium flex items-center ${
                  activeTab === 'contributions' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <TrendingUp size={16} className="mr-2" />
                Contributions
              </button>
              <button 
                onClick={() => setActiveTab('fines')}
                className={`border-b-2 py-4 px-1 text-sm font-medium flex items-center ${
                  activeTab === 'fines' 
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <AlertCircle size={16} className="mr-2" />
                Fines
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loadingTab ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {/* Personal Info Tab */}
                {activeTab === 'info' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Contact Information */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Phone size={20} className="mr-2 text-blue-600" />
                        Contact Information
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">Phone Number</label>
                          <p className="text-sm text-gray-900 mt-1">{member.phone_number}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">Email Address</label>
                          <p className="text-sm text-gray-900 mt-1">{member.email || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Identification */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <CreditCard size={20} className="mr-2 text-blue-600" />
                        Identification
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">Member Number</label>
                          <p className="text-sm text-gray-900 mt-1">{member.member_no || '-'}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">National ID</label>
                          <p className="text-sm text-gray-900 mt-1">{member.id_no || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Membership Details */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <Calendar size={20} className="mr-2 text-blue-600" />
                        Membership Details
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">Date Registered</label>
                          <p className="text-sm text-gray-900 mt-1">{formatDateDMY(member.date_registered)}</p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">Status</label>
                          <p className="text-sm text-gray-900 mt-1">
                            {member.is_active ? 'Active Member' : 'Inactive Member'}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 uppercase font-medium">Duration</label>
                          <p className="text-sm text-gray-900 mt-1">{memberSince}</p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    {member.additional_data && (
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                          <FileText size={20} className="mr-2 text-blue-600" />
                          Additional Information
                        </h3>
                        <div className="space-y-3">
                          {(() => {
                            let parsedData = member.additional_data;
                            if (typeof member.additional_data === 'string') {
                              try {
                                parsedData = JSON.parse(member.additional_data);
                              } catch (error) {
                                return <p className="text-sm text-gray-600">Unable to parse additional data</p>;
                              }
                            }
                            
                            if (typeof parsedData === 'object' && parsedData !== null && !Array.isArray(parsedData)) {
                              return Object.entries(parsedData).slice(0, 5).map(([key, value]) => (
                                <div key={key}>
                                  <label className="text-xs text-gray-500 uppercase font-medium">
                                    {key.replace(/_/g, ' ')}
                                  </label>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {value === null || value === undefined ? '-' : 
                                     typeof value === 'object' ? JSON.stringify(value) : 
                                     value.toString()}
                                  </p>
                                </div>
                              ));
                            }
                            return <p className="text-sm text-gray-600">No additional data available</p>;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Loans Tab */}
                {activeTab === 'loans' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Member Loans</h3>
                    {loans.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <DollarSign size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No loans found for this member</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="responsive-table min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan No.</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Principal</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Amount</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {loans.map((loan) => (
                              <tr key={loan.id} className="hover:bg-gray-50">
                                <td data-label="Loan No." className="px-4 py-3 text-sm text-gray-900">{loan.loan_no}</td>
                                <td data-label="Type" className="px-4 py-3 text-sm text-gray-600">{loan.loan_type}</td>
                                <td data-label="Principal" className="px-4 py-3 text-sm text-right text-gray-900">KES {formatCurrency(loan.principal)}</td>
                                <td data-label="Total Amount" className="px-4 py-3 text-sm text-right text-gray-900">KES {formatCurrency(loan.total_amount)}</td>
                                <td data-label="Balance" className="px-4 py-3 text-sm text-right font-semibold text-gray-900">KES {formatCurrency(loan.balance)}</td>
                                <td data-label="Status" className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    loan.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                                    loan.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {loan.status}
                                  </span>
                                </td>
                                <td data-label="Date" className="px-4 py-3 text-sm text-gray-600">{formatDateDMY(loan.start_date)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Contributions Tab */}
                {activeTab === 'contributions' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Member Contributions</h3>
                    {contributions.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <TrendingUp size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No contributions found for this member</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="responsive-table min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {contributions.map((contribution) => (
                              <tr key={contribution.id} className="hover:bg-gray-50">
                                <td data-label="Date" className="px-4 py-3 text-sm text-gray-900">{formatDateDMY(contribution.date)}</td>
                                <td data-label="Category" className="px-4 py-3 text-sm text-gray-600">{contribution.drive?.name || contribution.category || 'General'}</td>
                                <td data-label="Amount" className="px-4 py-3 text-sm text-right font-semibold text-green-600">KES {formatCurrency(contribution.amount)}</td>
                                <td data-label="Method" className="px-4 py-3 text-sm text-gray-600">{contribution.payment_method || '-'}</td>
                                <td data-label="Description" className="px-4 py-3 text-sm text-gray-600">{contribution.description || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Fines Tab */}
                {activeTab === 'fines' && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Member Fines</h3>
                    {fines.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <AlertCircle size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">No fines found for this member</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="responsive-table min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {fines.map((fine) => (
                              <tr key={fine.id} className="hover:bg-gray-50">
                                <td data-label="Date" className="px-4 py-3 text-sm text-gray-900">{formatDateDMY(fine.date)}</td>
                                <td data-label="Reason" className="px-4 py-3 text-sm text-gray-600">{fine.reason}</td>
                                <td data-label="Amount" className="px-4 py-3 text-sm text-right text-gray-900">KES {formatCurrency(fine.amount)}</td>
                                <td data-label="Paid" className="px-4 py-3 text-sm text-right text-green-600">KES {formatCurrency(fine.paid_amount)}</td>
                                <td data-label="Balance" className="px-4 py-3 text-sm text-right font-semibold text-red-600">KES {formatCurrency(fine.balance)}</td>
                                <td data-label="Status" className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    fine.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                    fine.status === 'PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {fine.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberProfilePage;
